import { useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { useCustomizationContext } from './useCardCustomization';
import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';

export const usePrintCardsProductQuantityOptions = (currentQuantity?: number, quantityChoicesOverride?: number[]) => {
  const [draft] = useCustomizationContext(draft => draft);
  const minorUnitsToDollars = useMinorUnitsToPriceString();
  const { priceCalculator, activeProduct } = useStationeryProductPricingContext();
  const { tq } = useTranslation('stationery');

  const options = useMemo(() => {
    const entries = [...(quantityChoicesOverride ?? activeProduct.supportedQuantities)];

    // Add '4' quantity option if we are viewing a paper card.
    if (activeProduct.productType === 'stationeryCard') {
      if (!entries.includes(4)) {
        entries.unshift(4);
      }
    }

    return entries
      .reduce(
        (acc, quantity) => {
          const pricingForQuantity = priceCalculator.getProductBasePrice(activeProduct.id, quantity);
          const totalPrice = pricingForQuantity.totalPriceInMinorUnits;
          const unitPrice = pricingForQuantity.unitPriceInMinorUnits;

          const pricePerCardString = minorUnitsToDollars(unitPrice, { canShowFreeLabel: true });
          const totalPriceString = minorUnitsToDollars(totalPrice, { canShowFreeLabel: true });

          acc.push({
            key: quantity.toString(),
            label: tq`${quantity} items at ${pricePerCardString} each`,
            value: quantity,
            pricePerCardString,
            totalPriceString,
          });
          return acc;
        },
        [] as Array<{ key: string; label: string; value: number; pricePerCardString: string; totalPriceString: string }>,
      )
      .sort((a, b) => {
        return a.value - b.value;
      });
  }, [quantityChoicesOverride, activeProduct, priceCalculator, minorUnitsToDollars, tq]);

  const selectedOption = useMemo(() => {
    return options.find(opt => opt.value === (currentQuantity ?? draft.config.customizations.quantity)) ?? options[0];
  }, [options, currentQuantity, draft.config.customizations.quantity]);

  return { options, selectedOption };
};
